<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { watch } from "vue";
import { z } from "zod";

import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import InputText from "primevue/inputtext";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseDialog from "@/components/ui/dialog/base-dialog.vue";

interface IEmits {
  (e: "submit", email: string): void;
}

const visible = defineModel<boolean>();
const emits = defineEmits<IEmits>();

const { t, k } = useTypedIntl();

const { handleSubmit, errors, defineField } = useForm({
  validationSchema: toTypedSchema(
    z.object({
      email: z.string().min(1, "required").email(),
    }),
  ),
  initialValues: {
    email: "",
  },
});

const [email] = defineField("email");

const submit = handleSubmit((value) => {
  emits("submit", value.email);
});

watch(visible, (newValue) => {
  if (newValue) {
    MixpanelService.trackEvent(EMixpanelEvent.FORGOTTEN_PASSWORD);
  }
});
</script>

<template>
  <BaseDialog
    v-model:visible="visible"
    modal
    size="small"
  >
    <template #header>
      <div class="flex flex-col">
        <h4 class="flex w-full font-semibold">
          {{ t(k.authentication.forgotPassword.title) }}
        </h4>
        <span class="text-text-secondary">
          {{ t(k.authentication.forgotPassword.subtitle) }}
        </span>
      </div>
    </template>

    <form @submit="submit">
      <div class="flex flex-col gap-2">
        <label class="font-medium">{{ t(k.authentication.forgotPassword.label) }}</label>
        <InputText
          v-model="email"
          autofocus
          data-test-id="forgot-password-username-input"
          :feedback="false"
          :invalid="!!errors.email"
          :placeholder="t(k.authentication.forgotPassword.placeholder)"
        />
        <small
          v-if="errors.email"
          class="text-red-500"
        >
          {{ t(k.veeValidate.messages.errors.invalidString.email) }}
        </small>
      </div>
    </form>

    <template #footer>
      <HospitalityButton
        class="w-full"
        data-test-id="submit-forgot-password-button"
        @click="submit"
      >
        <i class="pi pi-send mr-2" />
        {{ t(k.global.misc.send) }}
      </HospitalityButton>
    </template>
  </BaseDialog>
</template>
